import { Form, Formik } from 'formik';
import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';

import apiMethods from '../../apis';
import useGet from '../../services/hooks/http/useGet';
import useFetch from '../../services/hooks/http/useFetch';
import { stringSchema } from '../../services/schemas';
import { purchasesFields } from '../../store/catalogs/forms/purchases';
import Button from '../Buttons/Button/Button';
import LabelFieldFormik from '../Fields/LabelField/LabelFieldFormik';
import FormGrid from '../Form/FormGrid/FormGrid';
import NoticeCard from '../NoticeCard/NoticeCard';
import Context from '../../store/context';
import ItemsForm from '../Form/ItemsForm/ItemsForm';
import { formTypes } from '../../store/catalogs/forms';
import { taxesValueLabelList } from '../../store/catalogs/taxes';

import classes from './CreateOrEditPurchase.module.scss';
import { messageErrorForm, messageErrorFormPurchases } from '../../store/constants/messageErrors';

const validationSchema = Yup.object().shape({
  supplierName: stringSchema(messageErrorFormPurchases.supplierName),
  issueDate: stringSchema(messageErrorFormPurchases.issueDate),
  dueDate: stringSchema(messageErrorForm.date),
  invoiceAccountId: stringSchema(messageErrorForm.select),
  /* lineExtensionAmount: Yup.number().min(1, 'El monto debe ser mayor de 0').required('Campo obligatorio'),
  payableAmountAfterRetentions: Yup.number().min(1, 'El monto debe ser mayor de 0').required('Campo obligatorio'), */
  items: Yup.array().of(
    Yup.object().shape({
      description: stringSchema(messageErrorFormPurchases.item.description),
      priceAmount: Yup.number().min(1, 'El monto debe ser mayor que 0').required('Se debe digitar un monto'),
      invoiceQuantity: Yup.number()
        .min(1, messageErrorFormPurchases.item.quantity.notMatch)
        .required(messageErrorFormPurchases.item.quantity.required),
      lineExtensionAmount: Yup.number().min(1, 'El monto debe ser mayor que 0').required('Se debe digitar un monto'),
      tax: stringSchema('Campo obligatorio'),
      payableAmount: Yup.number().min(1, 'El monto debe ser mayor que 0').required('Se debe digitar un monto')
      //retefuenteAccountId: '',
      //reteicaAccountId: ''
    })
  )
});

const CreateOrEditPurchase = ({ updateBusinessFetch, refetch, supplierId, name, isEdit, purchasesData }) => {
  const valuesPurchases = {
    supplierName: isEdit ? purchasesData.supplierId : supplierId ? supplierId : '',
    supplierComplete: { label: isEdit ? name : '', value: isEdit ? supplierId : '' },
    cufe: isEdit ? purchasesData.cufe : '0',
    description: isEdit ? purchasesData.description : '',
    issueDate: isEdit ? purchasesData.issueDate : '',
    dueDate: isEdit ? purchasesData.dueDate : '',
    id: isEdit ? purchasesData.id : '',
    invoiceAccountId: isEdit ? purchasesData.invoiceAccountId : '',
    ...(isEdit
      ? { items: purchasesData.items }
      : {
          items: [
            {
              description: '',
              priceAmount: 0,
              invoiceQuantity: 0,
              lineExtensionAmount: 0,
              tax: 'none',
              payableAmount: 0,
              taxableAmount: 0,
              taxAmount: 0,
              isShow: true
            }
          ]
        }),
    ...(isEdit ? { purchaseId: purchasesData.purchaseId } : {})
    /* lineExtensionAmount: 0, */
    /* discounts: 0,
    payableAmountAfterRetentions: 0 */
  };
  const isNotEditable = isEdit ? purchasesData?.source === 'DIAN' : false;

  const { businessId } = useSelector(state => state.userData);

  const [fieldsState, setFieldsState] = useState([
    { ...purchasesFields.supplierName, disabled: isNotEditable },
    { ...purchasesFields.description, disabled: isNotEditable },
    { ...purchasesFields.issueDate, disabled: isNotEditable },
    purchasesFields.dueDate,
    purchasesFields.id,
    purchasesFields.invoiceAccountId
    /* purchasesFields.lineExtensionAmount,
    purchasesFields.discounts,
    purchasesFields.payableAmountAfterRetentions */
  ]);
  const [itemsState, setItemsState] = useState(
    isEdit
      ? purchasesData.items.map((item, idx) => ({
          idx,
          fields: [
            { key: `items.${idx}.description`, label: 'Nombre del item', disabled: isNotEditable },
            { key: `items.${idx}.priceAmount`, label: 'Valor', type: formTypes.money, disabled: isNotEditable },
            {
              key: `items.${idx}.invoiceQuantity`,
              label: 'Cantidad',
              type: formTypes.number,
              disabled: isNotEditable
            },
            {
              key: `items.${idx}.lineExtensionAmount`,
              label: 'Subtotal',
              type: formTypes.money,
              disabled: true
            },
            {
              key: `items.${idx}.tax`,
              label: 'Impuesto',
              type: formTypes.selectComplete,
              options: taxesValueLabelList
                .filter(({ value }) => value === '01')
                .concat({ value: 'none', label: 'Ninguna', percent: 0 }),
              disabled: isNotEditable
            },
            {
              key: `items.${idx}.payableAmount`,
              label: 'Total',
              type: formTypes.money,
              disabled: true
            },
          ]
        }))
      : [
          {
            idx: 0,
            fields: [
              { key: `items.${0}.description`, label: 'Nombre del item', disabled: isNotEditable },
              { key: `items.${0}.priceAmount`, label: 'Valor', type: formTypes.money, disabled: isNotEditable },
              {
                key: `items.${0}.invoiceQuantity`,
                label: 'Cantidad',
                type: formTypes.number,
                disabled: isNotEditable
              },
              {
                key: `items.${0}.lineExtensionAmount`,
                label: 'Subtotal',
                type: formTypes.money,
                disabled: true
              },
              {
                key: `items.${0}.tax`,
                label: 'Impuesto',
                type: formTypes.selectComplete,
                options: taxesValueLabelList
                  .filter(({ value }) => value === '01')
                  .concat({ value: 'none', label: 'Ninguna', percent: 0 }),
                disabled: isNotEditable
              },
              {
                key: `items.${0}.payableAmount`,
                label: 'Total',
                type: formTypes.money,
                disabled: true
              },
            ]
          }
        ]
  );

  const {
    isSuccess: getAllSuppliersSuccess,
    data: getAllSuppliersData,
    isError: getAllSuppliersError
  } = useGet(apiMethods.getAllSuppliers, [businessId]);

  const {
    modal: { hideModal, showError },
    toast: { showSuccess }
  } = useContext(Context);

  const {
    isSuccess: createPurchaseSuccess,
    sendRequest: createPurchase,
    isError: createPurchaseError,
    errorMessage: errorMessageCreate
  } = useFetch(apiMethods.createPurchase, [businessId]);

  const {
    isSuccess: updatePurchaseSuccess,
    sendRequest: updatePurchase,
    isError: updatePurchaseError,
    errorMessage: errorMessageupdatePurchase
  } = useFetch(apiMethods.updatePurchase, [businessId]);

  const onAccept = values => {
    const body = {
      ...values,
      //supplierName: values.supplierComplete.label,
      supplierId: values.supplierComplete.value,
      //payableAmount: values.payableAmountAfterRetentions,
      items: values.items
        ? values.items.map(item => ({
            ...item,
            taxes:
              item.tax === 'none'
                ? []
                : [
                    {
                      taxId: item.tax,
                      taxableAmount: item.lineExtensionAmount,
                      percent: item.taxComplete ? item.taxComplete.percent : item.percent,
                      taxAmount: item.taxAmount
                    }
                  ]
          }))
        : []
    };

    !isEdit ? createPurchase({ businessId, ...body }) : updatePurchase(businessId, values.purchaseId, body);
  };

  useEffect(() => {
    if (updatePurchaseSuccess) {
      refetch && refetch();
      updateBusinessFetch && updateBusinessFetch();
      showSuccess('Cuenta de cobro actualizada');
      hideModal();
    } else if (updatePurchaseError) {
      showError({ title: 'Actualizar cuenta de cobro', description: errorMessageupdatePurchase });
    }
  }, [
    updateBusinessFetch,
    updatePurchaseError,
    updatePurchaseSuccess,
    errorMessageupdatePurchase,
    hideModal,
    showError,
    showSuccess,
    refetch
  ]);

  useEffect(() => {
    if (createPurchaseSuccess) {
      refetch && refetch();
      updateBusinessFetch && updateBusinessFetch();
      showSuccess('Cuenta de cobro creada');
      hideModal();
    } else if (createPurchaseError) {
      showError({ title: 'Crear cuenta de cobro', description: errorMessageCreate });
    }
  }, [
    updateBusinessFetch,
    createPurchaseError,
    createPurchaseSuccess,
    errorMessageCreate,
    hideModal,
    showError,
    showSuccess,
    refetch
  ]);

  useEffect(() => {
    if (getAllSuppliersSuccess) {
      setFieldsState(fields => {
        const newFields = [...fields];
        newFields[0] = {
          ...newFields[0],
          options: getAllSuppliersData.map(({ supplierId, individualName }) => ({ value: supplierId, label: individualName }))
        };

        return newFields;
      });
    } else if (getAllSuppliersError) {
    }
  }, [getAllSuppliersData, getAllSuppliersError, getAllSuppliersSuccess]);

  const setField = (key, value, setFieldValue, values) => {
    if (key === 'supplierName') {
      setFieldValue('supplierName', value.value);
      setFieldValue('supplierComplete', value);
      return;
    } else if (['lineExtensionAmount', 'discounts'].includes(key)) {
      setFieldValue(
        'payableAmountAfterRetentions',
        key !== 'discounts'
          ? value - (values['discounts'] ? values['discounts'] : 0)
          : values['lineExtensionAmount'] - (value ? value : 0)
      );
    }

    setFieldValue(key, value);
  };

  const addItem = newItem => {
    setItemsState(fields => {
      const newFields = fields.concat(newItem);
      return newFields;
    });
  };

  const deleteItemsHandler = idx => {
    setItemsState(fields => {
      const newFields = fields
        .filter(item => item.idx !== idx)
        .map((item, index) => {
          return {
            idx: index,
            fields: item.fields.map(field => {
              const keySplit = field.key.split('.');
              return {
                ...field,
                key: `${keySplit[0]}.${index}.${keySplit[2]}`
              };
            })
          };
        });

      return newFields;
    });
  };

  return (
    <div>
      <span className='bold-20'>{isEdit ? 'Editar información cuenta de cobro' : 'Subir cuenta'}</span>
      {!isEdit && (
        <NoticeCard
          className={classes.notice}
          text='Si quieres subir una cuenta de cobro de un proveedor que no existe en Melo, debes crearlo primero desde la vista de Proveedores.'
        />
      )}
      <Formik initialValues={valuesPurchases} validationSchema={validationSchema} onSubmit={onAccept}>
        {({ setFieldValue, values, errors }) => {
          return (
            <Form className={`${classes.form}`}>
              <FormGrid
                onChange={(key, value) => setField(key, value, setFieldValue, values)}
                fields={fieldsState}
                Render={LabelFieldFormik}
              />
              <ItemsForm
                deleteItemsHandler={deleteItemsHandler}
                isNotEditable={isNotEditable}
                className={classes.items}
                items={itemsState}
                sendItem={addItem}
                values={values}
              />
              <div className={classes.button}>
                <Button name='createOrEdit' type='submit'>
                  {isEdit ? 'Guardar cuenta de cobro' : 'Subir cuenta'}
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default CreateOrEditPurchase;
